import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateLeaveRequestBody, LeaveRequestStatus } from '@mervonn/shared-types';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { leaveApi, type LeaveRequestsFilter, type LeaveRequestsPageQuery } from './api';

const keys = {
  all: ['leave'] as const,
  requestsPage: (q: LeaveRequestsPageQuery) => ['leave', 'requests-page', q] as const,
  count: (f: LeaveRequestsFilter) => ['leave', 'request-count', f] as const,
  window: (from: string, to: string, status?: LeaveRequestStatus) =>
    ['leave', 'window', from, to, status ?? 'any'] as const,
  balances: ['leave', 'balances'] as const,
  types: ['leave', 'types'] as const,
};

/** One page of requests. */
export function useLeaveRequestsPage(q: LeaveRequestsPageQuery, enabled = true) {
  return useQuery({ queryKey: keys.requestsPage(q), queryFn: () => leaveApi.requestsPage(q), placeholderData: keepPreviousData, enabled });
}

/** How many requests match, for a badge or a headline figure. Asks for one row; the total comes back anyway. */
export function useLeaveRequestCount(filter: LeaveRequestsFilter, enabled = true) {
  return useQuery({
    queryKey: keys.count(filter),
    queryFn: async () => (await leaveApi.requestsPage({ ...filter, page: 1, per_page: 1 })).meta.total,
    enabled,
  });
}

/** Every request overlapping a date window (`YYYY-MM-DD`), earliest first: a calendar draws them all. */
export function useLeaveWindow(from: string, to: string, status: LeaveRequestStatus | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.window(from, to, status),
    queryFn: () => fetchAllPages((n) => leaveApi.requestsPage({ from, to, status, page: n, per_page: 100 }), () => {}),
    enabled,
  });
}

export function useBulkApprove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => leaveApi.bulkApprove(ids),
    onSuccess: () => invalidateLeave(qc),
  });
}

export function useDepartmentRollup(enabled: boolean) {
  return useQuery({ queryKey: ['leave', 'department-rollup'] as const, queryFn: leaveApi.departmentRollup, enabled });
}

export function useLeaveBalances(enabled: boolean) {
  return useQuery({ queryKey: keys.balances, queryFn: leaveApi.balances, enabled });
}

export function useLeaveTypes(enabled: boolean) {
  return useQuery({
    queryKey: keys.types,
    queryFn: leaveApi.types,
    enabled,
    staleTime: 10 * 60_000,
  });
}

function invalidateLeave(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: keys.all });
}

export function useCreateLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeaveRequestBody) => leaveApi.create(body),
    onSuccess: () => invalidateLeave(qc),
  });
}

export function useApproveLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, override }: { id: string; override?: boolean }) =>
      leaveApi.approve(id, override),
    onSuccess: () => invalidateLeave(qc),
  });
}

export function useRejectLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => leaveApi.reject(id, reason),
    onSuccess: () => invalidateLeave(qc),
  });
}
